import React, {useEffect, useState} from 'react';
import {
  Image,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import {makeStyles} from '@rneui/themed';
import {observer} from 'mobx-react-lite';

import CartButton from '../../../../components/CartButton/CartButtonLoyaltyEnabled';
import MembersPriceLabel from '../../../../components/ProductTileFullHorizontal/MembersPriceLabel';
import SecondaryCalloutLabel from '../../../../components/ProductTileFullHorizontal/SecondaryCalloutLabel';
import Text from '../../../../components/ui-controls/Text';
import NoImage from '../../../../images/LMG_IMAGE_NOT_AVAILABLE_transparent.png';
import {AppRoute} from '../../../../models/AppRoute';
import useAppNavigation from '../../../../navigation/useAppNavigation';
import {useStores} from '../../../../stores/use-stores';
import {OrderLine} from '../../../../transport/apiServiceResponse';
import {Analytics} from '../../../../utils/analytics/analytics';
import {EventName} from '../../../../utils/analytics/events';
import {EventSourceType} from '../../../../utils/analytics/events';
import {toAUD} from '../../../../utils/functions';
import {getMemberPrice} from '../../../../utils/loyalty';
import {
  makeByLineText,
} from '../../../../utils/products';
import { getProductDisplayRules } from '../../../../utils/rewards';
import OrderLineItemStickers from '../OrderLineItemStickers';

type StyleProps = {
  /**
   * Take up full width. width: 100%
   */
  fullWidth?: boolean;
  hasShadow?: boolean;
  containerStyles?: ViewStyle;
};

type LastMinuteOptionTileCardProps = {
  item: OrderLine;
  disablePress?: boolean;
} & StyleProps;

const LastMinuteOptionTileCard = observer(
  ({
    item,
    fullWidth,
    disablePress = false,
    hasShadow = false,
    containerStyles = {}
  }: LastMinuteOptionTileCardProps) => {
    const styles = useStyles({fullWidth, hasShadow, containerStyles});
    const {cartStore} = useStores();
    const navigation = useAppNavigation();

    const [, setCount] = useState(0);

    const itemId = item.product_id;

    const memberPrice = getMemberPrice(item);
    const nonMemberPrice = getMemberPrice(item, 'non_member_price');
    const productDisplayRules = getProductDisplayRules(item);

    // Update the count value when it has been changed on
    // another screen.
    useEffect(() => {
      const qty = cartStore.getItemQty(itemId);
      setCount(qty);
    }, [cartStore, itemId, cartStore.currentOrder]);

    const handlePress = () => {
      if (disablePress) return;

      if (cartStore.updatingOrder) {
        return null;
      }
      navigation.navigate(AppRoute.PRODUCT, {product: item});
    };

    const byline = makeByLineText(item);

    const renderPrice = () => {
      const price = (nonMemberPrice?.sell_price) || item.sell_price_cents;
      return (
        <View style={styles.priceContainer}>
          <Text style={styles.price}>
            {`${toAUD(price)} `}
          </Text>
          <Text style={styles.eachesStyle} numberOfLines={1}>
            each
          </Text>
        </View>
      );
    }

    return (
      <TouchableOpacity
        onPress={handlePress}
        style={[styles.container]}
        testID="last-minute-option-tile-card">
        <View
          style={styles.imageHolder}
          testID="last-minute-option-tile-card-image-holder">
          <MembersPriceLabel
            externalSellType={memberPrice?.external_sell_type}
            productDisplayRules={productDisplayRules}
            center
            />
          {!!item?.links?.medium_image && (
            <Image
              testID="last-minute-option-tile-card-image"
              source={{uri: item.links.medium_image}}
              style={styles.tileImage}
              resizeMode="contain"
            />
          )}
          {!item.links?.medium_image && (
            <Image
              testID="last-minute-option-tile-card-image"
              source={NoImage}
              style={[styles.tileImage, {height: 140}]}
              resizeMode="contain"
            />
          )}
          <View
            style={{
              position: 'absolute',
              top: 24,
              right: 0,
              left: 0,
              bottom: 0,
            }}>
            <OrderLineItemStickers item={item} size='small'/>
          </View>
        </View>
        <View
          style={styles.textCard}
          testID="last-minute-option-tile-text-card">
          <Text style={styles.itemText} numberOfLines={2}>
            {item.name}
          </Text>
          <Text style={styles.itemQty}>{byline}</Text>
          <SecondaryCalloutLabel
            item={item}
          />
          {renderPrice()}
          <View style={styles.basketButtons}>
            <CartButton item={item} />
          </View>
        </View>
      </TouchableOpacity>
    );
  },
);

export default LastMinuteOptionTileCard;

const useStyles = makeStyles((theme, {fullWidth, hasShadow, containerStyles}: StyleProps) => ({
  opacity: {
    opacity: 0.2,
  },
  container: {
    padding: 8,
    alignItems: 'center',
    borderRadius: 4,
    borderWidth: 0.5,
    borderColor: '#CCC',
    backgroundColor: 'white',
    elevation: 7,
    marginHorizontal: 0,
    marginBottom: 0,
    ...(hasShadow
      ? {
          shadowColor: '#000',
          shadowOffset: {
            width: 0,
            height: 5,
          },
          shadowOpacity: 0.29,
          shadowRadius: 4.65,
        }
      : {}),
    ...(fullWidth
      ? {
          width: '100%',
        }
      : {
          width: 150,
        }),
    ...containerStyles,
  },
  imageHolder: {
    width: '100%',
  },
  tileImage: {
    maxWidth: 140,
    maxHeight: 140,
    width: '100%',
    aspectRatio: 1,
    marginTop: 4,
    alignSelf: 'center',
  },
  textCard: {
    width: '100%',
  },
  itemText: {
    fontWeight: 'bold',
    fontSize: 16,
    marginTop: 4,
    marginBottom: 4,
    height: 40,
    color: theme.colors.black,
  },
  buttonText: {
    color: 'white',
    textAlign: 'center',
  },
  itemQty: {
    color: 'grey',
    fontSize: 11,
  },
  button: {
    backgroundColor: theme.colour.background.button.primary,
    alignItems: 'center',
    borderRadius: 4,
    paddingVertical: 4,
    paddingHorizontal: 8,
    marginTop: 4,
    marginBottom: 8,
    width: '100%',
  },
  basketButtons: {
    alignItems: 'stretch',
    marginTop: 8,
  },
  priceContainer: {
    flexDirection: 'row',
    alignItems: 'baseline',
  },
  price: {
    fontSize: 20,
    fontWeight: 'bold',
    color: theme.colors.text?.heading.primary,
  },
  eachesStyle: {
    fontSize: 16,
    fontWeight: '400',
    color: theme.colors.text?.heading.primary,
  },
}));
